<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { getAlmInfo } from "@/backend/read/BackendReader";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import { useUser } from "@/composables/useUser";
import type { UserWithForeign } from "@/composables/useUserSearch";
import type { AuthUser } from "@/model/user";

const props = defineProps<{
  user: UserWithForeign;
  active?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}>();
const emit = defineEmits<{ select: [user: AuthUser] }>();

const { t } = useI18n();
const { userName } = useUser();

const tooltip = computed(() => {
  if (props.disabledTooltip) {
    return props.disabledTooltip;
  }
  switch (props.user.almMapping) {
    case "alm-only":
      return t("assignee.userNotMappedInPip", { almType: getAlmInfo().name });
    case "pip-only":
      return t("assignee.userNotMappedInAlm", { almType: getAlmInfo().name });
    default:
      return undefined;
  }
});
</script>

<template>
  <MenuListItem
    class="user-entry"
    :selected="active"
    :disabled
    :tooltip
    @click="emit('select', user)"
  >
    <template #before>
      <UserAvatar :user size="small" :class="{ 'disabled-avatar': disabled }" />
    </template>
    <template v-if="tooltip" #after>
      <SvgIcon name="message/warn" width="16" height="16" />
    </template>
    <span :class="{ 'team-user': !user.foreign }">
      {{ userName(user) }}
    </span>
  </MenuListItem>
</template>

<style lang="scss" scoped>
@use "@/styles/font";

.user-entry {
  .team-user {
    display: flex;
    align-items: center;
  }

  .disabled-avatar {
    opacity: 0.4;
  }
}
</style>
